import type { PanoVariants } from '@vrlearning/lesson-schema';
import type { Preparation } from '../api/session-client.js';
import type { Prefetcher } from './lobby.js';

/**
 * What the headset does when the session says a lesson is being prepared, has ended, has let this
 * student go, or when the pairing is revoked (ADR-038): main.ts's handlers, with every part
 * injected so the order of things is tested.
 */
export type FlowParts = {
  prefetcher: Pick<Prefetcher, 'prepare' | 'stop' | 'preparing'>;
  /** The lesson on this headset now: a class's (its session) or practice (none); undefined in the lobby. */
  running: () => { sessionId: string | undefined } | undefined;
  /** A photo the API no longer signs, or a 360° photo's cube, leaves this headset (media-cache.ts). */
  evict: (
    lesson: unknown,
    media: Readonly<Record<string, string>>,
    variants: Readonly<PanoVariants>,
  ) => void;
  /** The lobby, drawn again: its line says how far a prepared lesson is. */
  showLobby: () => void;
  setFreeTime: (allowed: boolean) => void;
  /** The whole exit: out of VR, back to the lobby, `device.left`, the name forgotten. */
  endLesson: () => void;
  /**
   * The lesson taken down here only: out of VR and back to the lobby, with no `device.left` (it would
   * land in the session now holding the headset).
   */
  leaveQuietly: () => void;
  /** The pupil's name let go of, here only (ADR-021): it belonged to the lesson that is over. */
  forgetName: () => void;
  forgetSchoolData: () => Promise<unknown>;
  /** The session socket closed for good (an unpaired headset has no session). */
  stopSession: () => void;
};

export class SessionFlow {
  readonly #parts: FlowParts;

  constructor(parts: FlowParts) {
    this.#parts = parts;
  }

  /** `session.prepare`: held in the lobby while the lesson's files come in. */
  prepared(preparation: Preparation): void {
    const parts = this.#parts;
    parts.setFreeTime(false);
    parts.evict(preparation.lesson, preparation.media, preparation.variants);
    const running = parts.running();
    // Another class's lesson, or a lesson practised alone, is over: this session holds the
    // headset now, as a running one does.
    if (running && running.sessionId !== preparation.sessionId) {
      parts.leaveQuietly();
      // Another class's pupil's name is not said to this session; a practising pupil's is kept.
      if (running.sessionId !== undefined) parts.forgetName();
    }
    parts.prefetcher.prepare(preparation);
    if (!parts.running()) parts.showLobby();
  }

  /** `session.ended`. */
  ended(): void {
    this.#leave();
  }

  /** `session.released`: free time begins. */
  released(): void {
    this.#leave(true);
  }

  /**
   * The pairing was revoked: the socket closes first, so no prepare or refresh starts anything
   * again; a lesson running -- a class's or practice -- is taken down quietly (no `device.left`:
   * there is no session to tell, and its photos and voice are the school's); the prefetch
   * settles; then the school's data is cleared again.
   */
  async revoked(): Promise<void> {
    this.#parts.stopSession();
    if (this.#parts.running()) this.#parts.leaveQuietly();
    await this.#parts.prefetcher.stop();
    await this.#parts.forgetSchoolData();
  }

  #leave(freeTime?: boolean): void {
    const parts = this.#parts;
    const wasPreparing = parts.prefetcher.preparing;
    void parts.prefetcher.stop();
    if (freeTime !== undefined) parts.setFreeTime(freeTime);
    // A prepared lesson ended before it started: nothing was built, nothing to leave.
    if (wasPreparing && !parts.running()) {
      parts.showLobby();
      return;
    }
    parts.endLesson();
  }
}
